/**
 * TourChatPanel — The unified AI chat surface for the guided-tour system.
 *
 * Renders when the engine's viewMode is 'chat'. This is THE floating chat
 * for the whole AI surface: the module assistant ('ai-assistant-open'),
 * the training coach ('ai-coach-open' / index "Chat" buttons), and tour
 * hand-offs all land here. It reuses the shared AIAssistantChat component
 * configured per module (assistantModuleConfigs) so the chat looks and
 * behaves identically to every module's dedicated assistant, while
 * useTourChat supplies module master-prompt + flow section_prompt context
 * to the same /api/v1/ai-assistant/chat endpoint.
 *
 * Header extras: a "Tours" button returns to the GuidedTourIndex (same
 * floating slot, same size), and the coach voice mute toggle.
 *
 * @created September 2026 — Phase 4 of the tour consolidation.
 * @updated Rebuilt on shared AIAssistantChat — single AI surface.
 */

'use client';

import { useCallback, useMemo } from 'react';
import { LayoutGrid, Volume2, VolumeX, X } from 'lucide-react';
import AIAssistantChat, {
  type AIMessage,
  type SessionHistoryItem,
} from '@/components/shared/AIAssistantChat';
import type { HeaderAction } from '@/components/modules/ModuleHeader';
import { assistantModuleConfigs } from '@/lib/ai/assistantModuleConfigs';
import { listChatSessions } from '@/lib/chatSessionPersistence';
import { useGuidedTour } from './GuidedTourProvider';
import { useTourChat } from './hooks/useTourChat';
import { useTourTTS } from './hooks/useTourTTS';
import { enterTrainingSandbox } from './utils/enterTrainingSandbox';
import type { ParsedUIAction } from './utils/uiActionParser';

// Coach actions become suggestion chips — sandbox chips require an explicit
// click (page reload), navigate/highlight are also re-clickable.
const actionLabel = (a: ParsedUIAction): string =>
  a.label ??
  (a.type === 'sandbox'
    ? 'Enter the Training Sandbox'
    : a.type === 'navigate' && a.tab
      ? `Go to ${a.tab.replace(/-/g, ' ')}`
      : 'Show me');

export function TourChatPanel() {
  const tour = useGuidedTour();
  const { engine, config, moduleKey, activeTab, flows } = tour;

  const schema = config?.schema ?? null;
  const moduleColor = config?.moduleConfig?.module_color ?? '#dc2626';
  const selectedFlow = engine.chatFlowId
    ? flows.find((f) => f.id === engine.chatFlowId) ?? null
    : null;
  const sectionPrompt = selectedFlow?.section_prompt;
  const isCoachMode = !!selectedFlow;
  const moduleConfig = assistantModuleConfigs[moduleKey];

  // ── TTS for chat responses ─────────────────────────────────────────────
  const tts = useTourTTS({
    currentStepId: null,
    currentNarrationText: '',
    isOpen: tour.isOpen,
    isMinimized: engine.isMinimized,
    voice: schema?.tts?.voice ?? schema?.audio?.default_voice ?? 'af_heart',
    autoPlay: false,
  });

  // Play base64 audio from chat responses
  const handlePlayAudio = useCallback(
    (base64: string) => {
      try {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/wav' });
        const blobUrl = URL.createObjectURL(blob);
        tts.playBlobUrl(blobUrl);
      } catch {
        // Silent failure — audio is non-critical
      }
    },
    [tts],
  );

  // ── Chat hook ──────────────────────────────────────────────────────────
  const chat = useTourChat({
    moduleKey,
    moduleContext: moduleConfig?.moduleContext,
    flowId: engine.chatFlowId,
    sectionPrompt,
    masterPrompt: config?.masterPrompt ?? undefined,
    enabled: tour.isOpen,
    activeTab,
    onNavigate: (tab) => {
      window.dispatchEvent(new CustomEvent('guided-tour-navigate', { detail: { tab } }));
    },
    onHighlight: (region, label) => {
      window.dispatchEvent(
        new CustomEvent('guided-tour-highlight', { detail: { region, label } }),
      );
    },
    onSandbox: () => {
      void enterTrainingSandbox({ moduleKey });
    },
    onPlayAudio: handlePlayAudio,
  });

  // ── Messages mapped to the shared chat component's shape ───────────────
  const messages: AIMessage[] = useMemo(
    () =>
      chat.messages.map((m) => ({
        id: m.id,
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
        timestamp: new Date(m.timestamp).toISOString(),
        actions: (m.actions ?? []).map((a, i) => ({
          id: `${m.id}-a${i}`,
          label: actionLabel(a),
          type: a.type,
          data: a,
        })),
      })),
    [chat.messages],
  );

  // ── Session history — lists persisted coaching sessions for this module ──
  const fetchHistory = useCallback(async (): Promise<SessionHistoryItem[]> => {
    const sessions = await listChatSessions(moduleKey, 20, 'coaching');
    return sessions.map((s) => ({
      id: s.sessionId,
      date: s.updatedAt,
      moduleContext: s.viewLabel ?? s.viewId ?? 'Coach',
      messageCount: s.messageCount,
      lastActivity: s.updatedAt,
    }));
  }, [moduleKey]);

  const handleLoadSession = useCallback(
    async (sessionId: string) => {
      await chat.loadSession(sessionId);
    },
    [chat],
  );

  // ── Header extras — "Tours" returns to the index; mute toggles TTS ─────
  const extraHeaderActions: HeaderAction[] = useMemo(
    () => [
      {
        id: 'tours',
        icon: LayoutGrid,
        label: 'Tours',
        title: 'Show tour index',
        onClick: engine.returnToIndexFromChat,
        variant: 'solid',
        showLabel: 'always',
      },
      {
        id: 'tts-mute',
        icon: tts.enabled ? Volume2 : VolumeX,
        title: tts.enabled ? 'Mute coach voice' : 'Enable coach voice',
        onClick: tts.toggleEnabled,
      },
    ],
    [engine.returnToIndexFromChat, tts.enabled, tts.toggleEnabled],
  );

  // Return null unless we're in chat mode (after all hooks are called)
  if (engine.viewMode !== 'chat' || !tour.isOpen || engine.isMinimized) return null;

  // ── Service unavailable state ──────────────────────────────────────────
  if (chat.serviceAvailable === false) {
    return (
      <div className="pointer-events-auto fixed bottom-4 right-4 z-[200] flex h-[min(80vh,680px)] w-[min(92vw,26rem)] animate-[tour-card-slide_0.4s_ease-out_both] flex-col overflow-hidden rounded-2xl border-2 bg-white/95 shadow-2xl backdrop-blur-md dark:bg-slate-800/95" style={{ borderColor: moduleColor }}>
        <div className="flex items-center justify-between border-b border-slate-200/80 px-3 py-2.5 dark:border-slate-700/80">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            {isCoachMode ? 'Training Coach' : 'AI Assistant'}
          </span>
          <button
            type="button"
            onClick={engine.returnToIndexFromChat}
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            title="Back to index"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            AI service is temporarily unavailable
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Please try again in a moment, or use the guided tour instead.
          </p>
          <button
            type="button"
            onClick={engine.returnToIndexFromChat}
            className="mt-2 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Back to tour index
          </button>
        </div>
      </div>
    );
  }

  const title = moduleConfig?.title ?? `AI Assistant — ${config?.moduleConfig?.module_name ?? moduleKey}`;

  return (
    <AIAssistantChat
      title={title}
      subtitle={moduleConfig?.subtitle ?? 'Powered by Total Access'}
      welcomeTitle={isCoachMode ? `Training Coach — ${selectedFlow!.label}` : moduleConfig?.welcomeTitle ?? title}
      welcomeMessage={
        isCoachMode
          ? `Ask me anything about ${selectedFlow!.label} or how to use this module.`
          : moduleConfig?.welcomeMessage ?? 'Ask me anything about this module.'
      }
      inputPlaceholder={moduleConfig?.inputPlaceholder ?? 'Ask me anything...'}
      moduleKey={moduleKey}
      moduleContext={moduleConfig?.moduleContext}
      messages={messages}
      isGenerating={chat.isGenerating}
      showCredits={false}
      onSendMessage={(text) => void chat.sendMessage(text)}
      onApplyAction={(action) => {
        const parsed = action.data as ParsedUIAction | undefined;
        if (parsed) chat.applyAction(parsed);
      }}
      onNewChat={chat.clearChat}
      showHistory
      onFetchHistory={fetchHistory}
      onLoadSession={handleLoadSession}
      onClose={engine.close}
      closable
      onFloatingMinimize={engine.minimize}
      coachToggle={false}
      extraHeaderActions={extraHeaderActions}
      floatingPanel
      floatingBorderColor={moduleColor}
      floatingBadgeLabel={isCoachMode ? 'Training Coach' : undefined}
      primaryColor={moduleColor}
      useUnifiedTheming={false}
      allowAttachments={false}
      className="h-[min(80vh,680px)] w-[min(92vw,26rem)]"
      disclaimerText="AI responses may not always be accurate. Verify important information."
    />
  );
}
